/**
 * Store Locator Script
 * @author: danweboptic
 * @lastModified: 2025-03-28 12:19:43 UTC
 * @version: Final with all refinements
 */

document.addEventListener('DOMContentLoaded', function() {
  // First check if required libraries are loaded
  if (typeof google === 'undefined') {
    console.error('Google Maps API is not loaded');
    return;
  }

  if (typeof markerClusterer === 'undefined') {
    console.error('MarkerClusterer library is not loaded');
    return;
  }

  // Get settings from global variable
  const settings = window.retailerFinderSettings || {};

  // Configuration
  const API_URL = settings.apiUrl || '/admin/api/retailers';
  const DEFAULT_LAT = parseFloat(settings.defaultLat) || 51.32946017198823;
  const DEFAULT_LNG = parseFloat(settings.defaultLng) || -0.590516176321099;
  const DEFAULT_ZOOM = 10;
  const SEARCH_ZOOM = settings.searchZoom || 12; // Default zoom level after search
  const MARKER_ZOOM = settings.markerZoom || 13; // Reduced zoom level when clicking on a retailer
  const DISTANCE_UNIT = settings.distanceUnit || 'miles';
  const DISTANCE_MULTIPLIER = DISTANCE_UNIT === 'kilometers' ? 1.60934 : 1;
  const DISTANCE_LABEL = DISTANCE_UNIT === 'kilometers' ? 'km' : 'miles';
  const MAX_RECENT_SEARCHES = 5;

  // Map animation settings
  const MAP_ANIMATION_DURATION = settings.mapAnimationDuration || 750; // in milliseconds
  const MAP_ANIMATION_EASING = settings.mapAnimationEasing || 'easeOutCubic'; // Easing function for map movement
  
  // Dynamic nearby retailers settings
  const ALWAYS_SHOW_RETAILERS = settings.alwaysShowRetailers !== false; // Always ensure retailers are visible on the map
  const MIN_RETAILERS_TO_SHOW = settings.minRetailersToShow || 3; // Minimum number of retailers to show on map

  // Level definitions
  const LEVEL_NAMES = {
    1: 'Gold Account',
    2: 'Retail Partner',
    3: 'Design Hub',
    4: 'V4 Showroom'
  };

  // Marker colors for each level
  const LEVEL_COLORS = {
    1: '#a89563', 2: '#2d2d2c', 3: '#346d43', 4: '#96632a'
  };

  // Optional custom marker images
  const MARKER_IMAGES = settings.markerImages || {};
  const DEFAULT_MARKER_SIZE = settings.defaultMarkerSize || { width: 30, height: 40 };

  // User location marker settings
  const USER_MARKER_COLOR = settings.userMarkerColor || '#6d8dbe';
  const USER_MARKER_SCALE = settings.userMarkerScale || 10;
  const USER_MARKER_BORDER = settings.userMarkerBorder || 3;
  const USER_MARKER_ANIMATION = settings.useMarkerAnimation !== false;

  // Location type zoom levels
  const LOCATION_TYPE_ZOOM = {
    country: 5, administrative_area_level_1: 7, administrative_area_level_2: 9,
    locality: 12, sublocality: 13, neighborhood: 14, route: 15, street_address: 16,
    postal_code: 12, default: 12
  };

  // Search this area settings
  const SEARCH_AREA_BUTTON_TEXT = settings.searchAreaButtonText || 'Search this area';
  const MAP_IDLE_DELAY = settings.mapIdleDelay || 500;

  // Text strings
  const LOADING_TEXT = settings.loadingText || 'Loading retailers...';
  const NO_RESULTS_TEXT = settings.noResultsText || 'No retailers found for your search.';
  const ERROR_LOADING_TEXT = settings.errorLoadingText || 'Error loading retailers. Please try again.';
  const LOCATION_ERROR_TEXT = settings.locationErrorText || 'Unable to get your location. Please enter a location manually.';
  const GEOLOCATION_NOT_SUPPORTED_TEXT = settings.geolocationNotSupportedText || 'Geolocation is not supported by your browser. Please enter a location manually.';
  const YOUR_LOCATION_TEXT = settings.yourLocationText || 'Your Location';
  const SHOWING_RETAILERS_TEXT = settings.showingRetailersText || 'Showing {count} retailers';
  const FAR_AWAY_NOTE = settings.farAwayNote || 'No retailers found nearby. Showing closest retailers.';

  // DOM Elements
  const searchInput = document.getElementById('retailer-search'),
        searchBtn = document.getElementById('search-btn'),
        useMyLocationBtn = document.getElementById('use-my-location-btn'),
        retailerList = document.getElementById('retailer-list'),
        countElement = document.getElementById('count'),
        mapElement = document.getElementById('retailer-map');

  // Google Maps variables
  let map, markers = [], bounds, geocoder, clusterer, searchBox, activeMarker = null;
  let searchAreaButtonContainer = null, searchAreaButton = null, mapIdleTimer = null;
  let lastMapCenter = null, userMovedMap = false, mapAnimationInProgress = false, noNearbyRetailersNote = null;

  // Data storage
  let retailers = [], allRetailers = [], userPosition = null, userMarker = null;
  let displayedRetailerCount = 0, openDetailItem = null;
  let isInitialLoad = true;

  // Local Storage & SVG
  const RETAILERS_CACHE_KEY = 'retailer_finder_retailers',
        RETAILERS_CACHE_TIMESTAMP_KEY = 'retailer_finder_retailers_timestamp',
        CACHE_EXPIRY_MINUTES = 60,
        PIN_SVG_PATH = 'M 0,0 C -2,-20 -10,-22 -10,-30 A 10,10 0 1,1 10,-30 C 10,-22 2,-20 0,0 z';

  // Initialize app
  initMap();
  initAutocomplete();
  createSearchAreaButton();

  // Event Listeners
  if (searchBtn) searchBtn.addEventListener('click', handleSearch);
  if (useMyLocationBtn) useMyLocationBtn.addEventListener('click', getUserLocation);

  function initAutocomplete() {
    try {
      // Support UK, Guernsey, Jersey, and Isle of Man
      const countryCodes = settings.countryCodes || ['GB', 'GG', 'JE', 'IM'];
      
      searchBox = new google.maps.places.Autocomplete(searchInput, {
        types: ['geocode'],
        componentRestrictions: { 
          country: countryCodes  // Pass as array for multiple countries
        }
      });

      searchBox.addListener('place_changed', function() {
        const place = searchBox.getPlace();
        if (!place.geometry) return;
        handlePlaceSelection(place);
      });
    } catch (error) {
      console.error('Error initializing autocomplete:', error);
    }
  }

  function initMap() {
    try {
      map = new google.maps.Map(mapElement, {
        center: { lat: DEFAULT_LAT, lng: DEFAULT_LNG },
        zoom: DEFAULT_ZOOM,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
        styles: [{
          featureType: "poi",
          elementType: "labels",
          stylers: [{ visibility: "off" }]
        }]
      });

      geocoder = new google.maps.Geocoder();
      bounds = new google.maps.LatLngBounds();

      map.addListener('dragstart', () => { userMovedMap = true; hideSearchAreaButton(); });
      map.addListener('zoom_changed', () => { userMovedMap = true; hideSearchAreaButton(); });
      map.addListener('idle', () => {
        if (userMovedMap) {
          if (mapIdleTimer) clearTimeout(mapIdleTimer);
          mapIdleTimer = setTimeout(() => {
            if (userMovedMap) { // Check again in case a programmatic search reset it
              const currentCenter = map.getCenter();
              if (!lastMapCenter || calculateDistance(lastMapCenter.lat(), lastMapCenter.lng(), currentCenter.lat(), currentCenter.lng()) > 0.5) {
                showSearchAreaButton();
              }
            }
          }, MAP_IDLE_DELAY);
        }
      });

      clusterer = new markerClusterer.MarkerClusterer({
        map,
        markers: [],
        algorithm: new markerClusterer.SuperClusterAlgorithm({ maxZoom: 16, radius: 60 })
      });

      userPosition = { lat: DEFAULT_LAT, lng: DEFAULT_LNG };
      isInitialLoad = true;
      loadRetailers();

    } catch (error) {
      console.error('Error initializing map:', error);
      mapElement.innerHTML = 'Error loading map. Please refresh the page.';
    }
  }

  function createNoNearbyRetailersNote() {
    noNearbyRetailersNote = document.createElement('div');
    noNearbyRetailersNote.className = 'no-nearby-retailers-note';
    noNearbyRetailersNote.innerHTML = FAR_AWAY_NOTE;
    noNearbyRetailersNote.style.display = 'none';
    if (retailerList && retailerList.parentNode) {
      retailerList.parentNode.insertBefore(noNearbyRetailersNote, retailerList);
    }
  }

  function showNoNearbyRetailersNote() {
    if (!noNearbyRetailersNote) createNoNearbyRetailersNote();
    if (noNearbyRetailersNote) noNearbyRetailersNote.style.display = 'block';
  }

  function hideNoNearbyRetailersNote() {
    if (noNearbyRetailersNote) noNearbyRetailersNote.style.display = 'none';
  }

  function createSearchAreaButton() {
    searchAreaButtonContainer = document.createElement('div');
    searchAreaButtonContainer.className = 'search-area-button-container';
    searchAreaButtonContainer.style.display = 'none';
    searchAreaButton = document.createElement('div');
    searchAreaButton.className = 'search-this-area-button btn btn--primary btn--small';
    searchAreaButton.innerHTML = SEARCH_AREA_BUTTON_TEXT;
    searchAreaButtonContainer.appendChild(searchAreaButton);
    map.controls[google.maps.ControlPosition.TOP_CENTER].push(searchAreaButtonContainer);
    searchAreaButton.addEventListener('click', function() {
      const center = map.getCenter();
      isInitialLoad = false;
      userMovedMap = false;
      userPosition = { lat: center.lat(), lng: center.lng() };
      lastMapCenter = center;
      hideSearchAreaButton();
      addUserMarker(userPosition);
      if (allRetailers.length > 0) {
        updateRetailers();
        ensureRetailersVisible();
        scrollListToTop();
      } else {
        loadRetailers();
      }
    });
  }

  function showSearchAreaButton() { if (searchAreaButtonContainer) searchAreaButtonContainer.style.display = 'block'; }
  function hideSearchAreaButton() { if (searchAreaButtonContainer) searchAreaButtonContainer.style.display = 'none'; }

  function addUserMarker(position) {
    if (userMarker) userMarker.setMap(null);
    const userIcon = {
      path: google.maps.SymbolPath.CIRCLE, fillColor: USER_MARKER_COLOR, fillOpacity: 0.8,
      strokeColor: '#FFFFFF', strokeWeight: USER_MARKER_BORDER, scale: USER_MARKER_SCALE
    };
    userMarker = new google.maps.Marker({
      position, map, icon: userIcon, zIndex: 1000, title: YOUR_LOCATION_TEXT,
      animation: USER_MARKER_ANIMATION ? google.maps.Animation.DROP : null
    });
  }

  function getUserLocation() {
    if (navigator.geolocation) {
      retailerList.innerHTML = `<div class="retailer-finder__loading">Getting your location...</div>`;
      navigator.geolocation.getCurrentPosition(
        position => {
          isInitialLoad = false;
          userMovedMap = false;
          userPosition = { lat: position.coords.latitude, lng: position.coords.longitude };
          localStorage.setItem('retailer_finder_lat', userPosition.lat);
          localStorage.setItem('retailer_finder_lng', userPosition.lng);
          animateMapMove(userPosition, SEARCH_ZOOM);
          addUserMarker(userPosition);
          lastMapCenter = map.getCenter();
          hideSearchAreaButton();
          loadRetailers();
          scrollListToTop();
        },
        error => {
          console.error("Error getting location", error);
          retailerList.innerHTML = `<div class="no-results">${LOCATION_ERROR_TEXT}</div>`;
        }
      );
    } else {
      retailerList.innerHTML = `<div class="no-results">${GEOLOCATION_NOT_SUPPORTED_TEXT}</div>`;
    }
  }

  function handlePlaceSelection(place) {
    isInitialLoad = false;
    userMovedMap = false;
    userPosition = { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() };
    saveRecentSearch(place.formatted_address, userPosition);
    animateMapMove(userPosition);
    addUserMarker(userPosition);
    lastMapCenter = map.getCenter();
    hideSearchAreaButton();
    if (allRetailers.length > 0) {
      updateRetailers();
      if (place.geometry.viewport) map.fitBounds(place.geometry.viewport);
      else {
        map.setCenter(userPosition);
        let zoomLevel = SEARCH_ZOOM;
        if (place.types && place.types.length > 0) {
          for (const type of place.types) {
            if (LOCATION_TYPE_ZOOM[type]) { zoomLevel = LOCATION_TYPE_ZOOM[type]; break; }
          }
        }
        map.setZoom(zoomLevel);
      }
      ensureRetailersVisible();
      scrollListToTop();
    } else { loadRetailers(); }
  }

  function scrollListToTop() {
    if (retailerList) retailerList.scrollTop = 0;
  }

  function animateMapMove(position, targetZoom) {
    if (mapAnimationInProgress) return;
    mapAnimationInProgress = true;
    const currentZoom = map.getZoom();
    const needsZoomChange = (targetZoom !== undefined && targetZoom !== currentZoom);
    map.panTo(position);
    const panEndListener = google.maps.event.addListenerOnce(map, 'idle', function() {
      if (needsZoomChange) {
        smoothZoomTo(targetZoom);
      } else {
        mapAnimationInProgress = false;
      }
    });
    setTimeout(function() {
      google.maps.event.removeListener(panEndListener);
      if (needsZoomChange && mapAnimationInProgress) smoothZoomTo(targetZoom);
      else mapAnimationInProgress = false;
    }, MAP_ANIMATION_DURATION + 200);
  }

  function smoothZoomTo(targetZoom) {
    const currentZoom = map.getZoom();
    if (currentZoom === targetZoom) {
      mapAnimationInProgress = false;
      return;
    }
    const zoomingIn = targetZoom > currentZoom;
    const step = zoomingIn ? 1 : -1;
    const perLevelDuration = MAP_ANIMATION_DURATION / Math.abs(targetZoom - currentZoom);
    function zoomStep() {
      const nextZoom = map.getZoom() + step;
      map.setZoom(nextZoom);
      if ((zoomingIn && nextZoom < targetZoom) || (!zoomingIn && nextZoom > targetZoom)) {
        setTimeout(zoomStep, perLevelDuration);
      } else {
        mapAnimationInProgress = false;
      }
    }
    setTimeout(zoomStep, 100);
  }

  function ensureRetailersVisible() {
    if (!userPosition || retailers.length === 0 || !ALWAYS_SHOW_RETAILERS || isInitialLoad) return;
    const currentBounds = map.getBounds();
    let retailersInView = 0;
    const sortedRetailers = [...retailers].sort((a, b) => a.distance - b.distance);
    const closestRetailers = sortedRetailers.slice(0, MIN_RETAILERS_TO_SHOW);
    if (currentBounds) {
      for (const marker of markers) {
        if (currentBounds.contains(marker.getPosition())) retailersInView++;
      }
    }
    if (retailersInView < MIN_RETAILERS_TO_SHOW) {
      const newBounds = new google.maps.LatLngBounds();
      newBounds.extend(new google.maps.LatLng(userPosition.lat, userPosition.lng));
      for (const retailer of closestRetailers) {
        newBounds.extend(new google.maps.LatLng(parseFloat(retailer.latitude), parseFloat(retailer.longitude)));
      }
      map.fitBounds(newBounds, { padding: 50 });
      if (closestRetailers.length > 0 && closestRetailers[0].distance > 25) showNoNearbyRetailersNote();
      else hideNoNearbyRetailersNote();
    }
  }

  function handleSearch() {
    const query = searchInput.value.trim();
    if (!query) return;
    retailerList.innerHTML = `<div class="retailer-finder__loading">Searching for location...</div>`;
    geocoder.geocode({ 'address': query }, (results, status) => {
      if (status === 'OK') {
        isInitialLoad = false;
        userMovedMap = false;
        userPosition = { lat: results[0].geometry.location.lat(), lng: results[0].geometry.location.lng() };
        saveRecentSearch(query, userPosition);
        animateMapMove(userPosition);
        addUserMarker(userPosition);
        lastMapCenter = map.getCenter();
        hideSearchAreaButton();
        if (allRetailers.length > 0) {
          updateRetailers();
          if (results[0].geometry.viewport) map.fitBounds(results[0].geometry.viewport);
          else {
            map.setCenter(userPosition);
            let zoomLevel = SEARCH_ZOOM;
            if (results[0].types) {
              for (const type of results[0].types) {
                if (LOCATION_TYPE_ZOOM[type]) { zoomLevel = LOCATION_TYPE_ZOOM[type]; break; }
              }
            }
            map.setZoom(zoomLevel);
          }
          ensureRetailersVisible();
          scrollListToTop();
        } else { loadRetailers(); }
      } else {
        console.error('Geocode was not successful:', status);
        retailerList.innerHTML = `<div class="no-results">Location not found. Please try a different search.</div>`;
      }
    });
  }

  async function loadRetailers() {
    const cachedRetailers = getCachedRetailers();
    if (cachedRetailers) {
      allRetailers = cachedRetailers;
      console.log('Retailers loaded from cache.');
      updateRetailers();
      if (userPosition && !isInitialLoad) centerMapOnSearch();
      scrollListToTop();
      fetchRetailersFromAPI(true);
    } else {
      fetchRetailersFromAPI();
    }
  }

  async function fetchRetailersFromAPI(background = false) {
    try {
      if (!background) retailerList.innerHTML = `<div class="retailer-finder__loading">${LOADING_TEXT}</div>`;
      const response = await fetch(API_URL);
      if (!response.ok) throw new Error(`API request failed with status ${response.status}`);
      const data = await response.json();
      if (!Array.isArray(data)) throw new Error('API did not return an array of retailers');
      allRetailers = data;
      if (allRetailers.length === 0) {
        retailerList.innerHTML = `<div class="no-results">${NO_RESULTS_TEXT}</div>`;
        updateCountDisplay(0);
        return;
      }
      cacheRetailers(allRetailers);
      updateRetailers();
      if (userPosition && !isInitialLoad) centerMapOnSearch();
      scrollListToTop();
    } catch (error) {
      console.error('Error fetching retailers:', error);
      retailerList.innerHTML = `<div class="retailer-finder__error">${ERROR_LOADING_TEXT}</div>`;
    }
  }

  function getCachedRetailers() {
    const cachedData = localStorage.getItem(RETAILERS_CACHE_KEY);
    const timestamp = localStorage.getItem(RETAILERS_CACHE_TIMESTAMP_KEY);
    if (!cachedData || !timestamp) return null;
    const expiry = CACHE_EXPIRY_MINUTES * 60 * 1000;
    if (Date.now() - timestamp > expiry) {
      localStorage.removeItem(RETAILERS_CACHE_KEY);
      localStorage.removeItem(RETAILERS_CACHE_TIMESTAMP_KEY);
      return null;
    }
    try { return JSON.parse(cachedData); } 
    catch (error) { console.error('Error parsing cached retailers:', error); return null; }
  }

  function cacheRetailers(retailers) {
    try {
      localStorage.setItem(RETAILERS_CACHE_KEY, JSON.stringify(retailers));
      localStorage.setItem(RETAILERS_CACHE_TIMESTAMP_KEY, Date.now().toString());
      console.log('Retailers cached.');
    } catch (error) { console.error('Error caching retailers:', error); }
  }

  function centerMapOnSearch() {
    if (!userPosition) return;
    map.setCenter(userPosition);
    map.setZoom(SEARCH_ZOOM);
    if (retailers.length > 0) ensureRetailersVisible();
    lastMapCenter = map.getCenter();
  }

  function updateRetailers() {
    displayedRetailerCount = 0;
    openDetailItem = null;

    if (!userPosition) {
      retailers = []; // Should not happen with default position set
    } else {
      const v4Showrooms = [], designHubs = [], partners = [], goldAccounts = [];
      allRetailers.forEach(retailer => {
        const distance = calculateDistance(userPosition.lat, userPosition.lng, parseFloat(retailer.latitude), parseFloat(retailer.longitude)) * DISTANCE_MULTIPLIER;
        const processedRetailer = { ...retailer, distance };
        const level = parseInt(retailer.level) || 0;
        if (level === 4) v4Showrooms.push(processedRetailer);
        else if (level === 3) designHubs.push(processedRetailer);
        else if (level === 2) partners.push(processedRetailer);
        else if (level === 1) goldAccounts.push(processedRetailer);
      });

      const sortByDistance = (a, b) => a.distance - b.distance;
      v4Showrooms.sort(sortByDistance);
      designHubs.sort(sortByDistance);
      partners.sort(sortByDistance);
      goldAccounts.sort(sortByDistance);

      retailers = [
        ...v4Showrooms.slice(0, 1),
        ...designHubs.slice(0, 2),
        ...partners.slice(0, 3),
        ...goldAccounts.slice(0, 4)
      ];
    }

    updateCountDisplay(retailers.length);
    displayRetailers();
    updateMarkers();
  }

  function updateCountDisplay(count) {
    if (countElement) countElement.textContent = SHOWING_RETAILERS_TEXT.replace('{count}', count);
  }

  function calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 3959; // Radius of the Earth in miles
    const dLat = toRad(lat2 - lat1), dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  function toRad(value) { return value * Math.PI / 180; }

  function displayRetailers() {
    retailerList.innerHTML = '';
    if (retailers.length === 0) {
      retailerList.innerHTML = `<div class="no-results">${NO_RESULTS_TEXT}</div>`;
      updateCountDisplay(0);
      return;
    }
    retailers.forEach((retailer, index) => addRetailerToList(retailer, index));
    displayedRetailerCount = retailers.length;
    updateCountDisplay(displayedRetailerCount);
  }

  function addRetailerToList(retailer, index) {
    const retailerElement = document.createElement('div');
    retailerElement.className = 'retailer-item';
    retailerElement.dataset.index = index;
    const levelName = retailer.level ? LEVEL_NAMES[retailer.level] || '' : '';
    const levelClass = retailer.level ? `level-${retailer.level}` : '';
    if (retailer.level) retailerElement.classList.add(`level-${retailer.level}-item`);
    if (parseInt(retailer.level) === 4) retailerElement.classList.add('premium-retailer');

    const distanceHTML = !isInitialLoad && retailer.distance !== undefined
      ? `<div class="retailer-distances"><span class="retailer-distance">${retailer.distance.toFixed(1)} ${DISTANCE_LABEL}</span></div>`
      : '';

    retailerElement.innerHTML = `
      <div class="retailer-header">
        <div class="retailer-main-info">
          <div class="retailer-name">
            ${levelName ? `<div class="retailer-level ${levelClass}" title="${levelName}">${levelName}</div>` : ''}
            ${retailer.name}
          </div>
          ${distanceHTML}
        </div>
        <button class="retailer-toggle-btn" aria-label="Show details"></button>
      </div>
      <div class="retailer-details" style="display:none">
        <div class="retailer-address"><strong>${retailer.name}</strong><br>${retailer.address}, ${retailer.city}, ${retailer.postcode}</div>
        ${retailer.phone ? `<div class="retailer-phone"><strong>Tel:</strong> <a href="tel:${retailer.phone}">${retailer.phone}</a></div>` : ''}
        ${retailer.website ? `<div class="retailer-website"><strong>Website:</strong> <a href="${retailer.website}" target="_blank" rel="noopener">${retailer.website}</a></div>` : ''}
        <div class="retailer-link"><a href="/pages/retailer?id=${retailer.slug}" target="_blank" class="btn btn--primary btn--extra-small">More Details</a></div>
      </div>
    `;

    const toggleBtn = retailerElement.querySelector('.retailer-toggle-btn');
    const detailsDiv = retailerElement.querySelector('.retailer-details');

    retailerElement.addEventListener('click', (e) => {
      if (e.target.closest('a')) return;
      if (e.target === toggleBtn) e.stopPropagation();

      const isVisible = detailsDiv.style.display !== 'none';
      if (!isVisible && openDetailItem && openDetailItem !== detailsDiv) {
          const parentItem = openDetailItem.closest('.retailer-item');
          if (parentItem) {
              const btn = parentItem.querySelector('.retailer-toggle-btn');
              if(btn) btn.classList.remove('active');
          }
          openDetailItem.style.display = 'none';
      }

      detailsDiv.style.display = isVisible ? 'none' : 'block';
      toggleBtn.classList.toggle('active', !isVisible);
      openDetailItem = isVisible ? null : detailsDiv;
      highlightRetailer(index);
    });

    retailerList.appendChild(retailerElement);
  }

  function updateMarkers() {
    clearMarkers();
    bounds = new google.maps.LatLngBounds();
    if (userPosition && !isInitialLoad) bounds.extend(userPosition);

    const newMarkers = [];
    for (let i = 0; i < retailers.length; i++) {
      const retailer = retailers[i];
      const position = { lat: parseFloat(retailer.latitude), lng: parseFloat(retailer.longitude) };
      let markerIcon;
      if (retailer.level && MARKER_IMAGES[retailer.level]) {
        markerIcon = {
          url: MARKER_IMAGES[retailer.level],
          scaledSize: new google.maps.Size(DEFAULT_MARKER_SIZE.width, DEFAULT_MARKER_SIZE.height),
          origin: new google.maps.Point(0, 0),
          anchor: new google.maps.Point(DEFAULT_MARKER_SIZE.width / 2, DEFAULT_MARKER_SIZE.height)
        };
      } else {
        const markerColor = retailer.level && LEVEL_COLORS[retailer.level] ? LEVEL_COLORS[retailer.level] : '#FF5722';
        markerIcon = {
          path: PIN_SVG_PATH, fillColor: markerColor, fillOpacity: 1,
          strokeColor: '#FFFFFF', strokeWeight: 2, scale: 1.2, anchor: new google.maps.Point(0, 30)
        };
      }
      const marker = new google.maps.Marker({
        position, map, icon: markerIcon, title: retailer.name, optimized: true, retailerId: retailer.id
      });
      marker.addListener('click', () => {
          const originalIndex = retailers.findIndex(r => r.id === retailer.id);
          if (originalIndex !== -1) {
              const item = retailerList.querySelector(`[data-index="${originalIndex}"]`);
              if (item) item.click(); // Simulate a click on the row to handle everything
          }
      });
      newMarkers.push(marker);
    }
    markers = newMarkers;
    clusterer.clearMarkers();
    clusterer.addMarkers(markers);
    if (userMarker) userMarker.setZIndex(1001);
  }

  function highlightMarker(marker) {
    if (activeMarker && activeMarker !== marker) resetMarkerHighlight(activeMarker);
    if (!marker.originalIcon) marker.originalIcon = { ...marker.getIcon() };
    let highlightedIcon;
    if (marker.getIcon().url) {
      highlightedIcon = { ...marker.getIcon() };
      const currentSize = marker.getIcon().scaledSize;
      highlightedIcon.scaledSize = new google.maps.Size(currentSize.width * 1.2, currentSize.height * 1.2);
      highlightedIcon.anchor = new google.maps.Point(currentSize.width * 1.2 / 2, currentSize.height * 1.2);
    } else {
      highlightedIcon = { ...marker.getIcon() };
      highlightedIcon.scale = 1.5;
      highlightedIcon.strokeWeight = 3;
    }
    marker.setIcon(highlightedIcon);
    marker.setZIndex(1000);
    activeMarker = marker;
  }

  function resetMarkerHighlight(marker) {
    if (marker && marker.originalIcon) {
      marker.setIcon(marker.originalIcon);
      marker.setZIndex(null);
    }
  }

  function clearMarkers() {
    markers.forEach(marker => marker.setMap(null));
    markers = [];
    activeMarker = null;
  }

  function highlightRetailer(index) {
    const retailerAtIndex = retailers[index];
    if (!retailerAtIndex) return;
    
    const marker = markers.find(m => m.retailerId === retailerAtIndex.id);
    if (marker) {
      highlightMarker(marker);
      if (!map.getCenter().equals(marker.getPosition())) {
        animateMapMove(marker.getPosition(), MARKER_ZOOM);
      }
    }
    
    Array.from(retailerList.getElementsByClassName('retailer-item')).forEach(item => item.classList.remove('active'));
    const activeItem = retailerList.querySelector(`[data-index="${index}"]`);
    if(activeItem) activeItem.classList.add('active');
  }

  function saveMapState() {
    if (map) {
      const center = map.getCenter();
      const zoom = map.getZoom();
      localStorage.setItem('map_center_lat', center.lat());
      localStorage.setItem('map_center_lng', center.lng());
      localStorage.setItem('map_zoom', zoom);
    }
  }

  function saveRecentSearch(query, position) {
    try {
      let recentSearches = JSON.parse(localStorage.getItem('recent_searches') || '[]');
      const newSearch = { query, position, timestamp: new Date().toISOString() };
      recentSearches.unshift(newSearch);
      if (recentSearches.length > MAX_RECENT_SEARCHES) {
        recentSearches = recentSearches.slice(0, MAX_RECENT_SEARCHES);
      }
      localStorage.setItem('recent_searches', JSON.stringify(recentSearches));
    } catch (error) { console.error('Error saving recent search:', error); }
  }

  window.addEventListener('beforeunload', saveMapState);
});